<script setup lang="ts">
import { Color } from '@previo/codebooks'
import { computed, inject } from 'vue'
import { type Dayjs } from 'dayjs'
import { useDateTimeRangePicker } from '../composables/DateTimeRangePickerState'
import { DateTimeRange } from '../model/DateTimeRange'
import { DateTimeRangeSelectionService } from '../services/DateTimeRangeSelectionService'
import PTextFormBody from '../../typography/form/PTextFormBody.vue'

type Props = {
  date?: Dayjs | null
  dim?: boolean
  defaultTimeFrom?: string
  defaultTimeTo?: string
}

const props = withDefaults(defineProps<Props>(), {
  dim: false,
})

const model = defineModel<DateTimeRange>({ default: new DateTimeRange() })

const navigation = useDateTimeRangePicker()

const selectionService = inject<DateTimeRangeSelectionService>('dateTimeRangeSelectionService')

if (!selectionService) {
  throw new Error('DateTimeRangeSelectionService must be provided')
}

const color = computed<Color>(() => {
  if (isSelected.value) {
    return Color.WHITE
  }

  if (props.dim || isDisabled.value) {
    return Color.GREY_300
  }

  return Color.GREY_500
})

const isSelected = computed<boolean>(() => {
  if (!props.date) {
    return false
  }

  return (
    (model.value.from?.tz().isSame(props.date, 'date') ||
      model.value.to?.tz().isSame(props.date, 'date')) ??
    false
  )
})

const isToday = computed<boolean>(() => {
  if (!props.date) {
    return false
  }

  return props.date.isTzToday()
})

const isDisabled = computed<boolean>(() => {
  if (!props.date) {
    return true
  }

  const min = navigation.getMin()
  const max = navigation.getMax()

  return (
    (!!min && props.date.isBefore(min, 'date')) ||
    (!!max && props.date.isAfter(max, 'date')) ||
    exceedsMaxRange.value
  )
})

const isSelectingRangeEnd = computed<boolean>(() => {
  if (navigation.isSelectingTo()) {
    return true
  }

  return !navigation.isSelectingFrom() && !model.value.to
})

const exceedsMaxRange = computed<boolean>(() => {
  const maxRangeDays = navigation.getMaxRangeDays()
  const { from, to } = model.value

  if (!props.date || maxRangeDays === null) {
    return false
  }

  if (isSelectingRangeEnd.value) {
    if (!from) {
      return false
    }

    const lastAllowedDate = from.tz().add(maxRangeDays, 'day')

    return props.date.isAfter(lastAllowedDate, 'date')
  }

  if (from || !to) {
    return false
  }

  const firstAllowedDate = to.tz().subtract(maxRangeDays, 'day')

  return props.date.isBefore(firstAllowedDate, 'date')
})

const isFrom = computed<boolean>(() => {
  if (!props.date) {
    return false
  }

  return model.value.from?.tz().isSame(props.date, 'date') || false
})

const isTo = computed<boolean>(() => {
  if (!props.date) {
    return false
  }

  return model.value.to?.tz().isSame(props.date, 'date') || false
})

const isHighlighted = computed<boolean>(() => {
  if (!props.date) {
    return false
  }

  return model.value.from && model.value.to
    ? props.date
        .tz()
        .startOf('day')
        ?.isBetween(
          model.value.from.tz().startOf('day'),
          model.value.to.tz().startOf('day'),
          null,
          '[]',
        )
    : false
})

const onClick = (): void => {
  if (!props.date || isDisabled.value) {
    return
  }

  selectionService.handleDateSelection(props.date, model.value)
}
</script>

<template>
  <div
    class="ds-date-time-range-picker__date-cell-wrapper p-flex p-flex-center p-cursor-pointer p-non-selectable"
  >
    <div
      v-for="index in 2"
      :key="index"
      :class="[
        'ds-date-time-range-picker__block',
        {
          'ds-date-time-range-picker__block--highlighted': isHighlighted,
          'ds-date-time-range-picker__block--is-from': isFrom,
          'ds-date-time-range-picker__block--is-to': isTo,
        },
      ]"
    ></div>
    <div
      :class="[
        'ds-date-time-range-picker__date-cell p-flex p-flex-center',
        {
          'ds-date-time-range-picker__date-cell--selected': isSelected,
          'ds-date-time-range-picker__date-cell--disabled': isDisabled,
          'ds-date-time-range-picker__date-cell--today': isToday,
          'ds-date-time-range-picker__date-cell--is-from': isFrom,
          'ds-date-time-range-picker__date-cell--is-to': isTo,
          'ds-date-time-range-picker__date-cell--is-both': isFrom && isTo,
        },
      ]"
      @click="onClick"
    >
      <p-text-form-body :text="date?.format('D') || ''" :color="color" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '../../../assets/scss/shared/variables/ds.radius';
@import '../../../assets/scss/shared/ds.variables.module';
@import '../../../assets/scss/shared/brand.module';
@import '../../../assets/scss/shared/mixins';

$mobile-cell-height: 42px;
$laptop-cell-height: 36px;

.ds-date-time-range-picker {
  &__date-cell-wrapper {
    height: $mobile-cell-height;
    position: relative;

    @include laptopAndLarge() {
      height: $laptop-cell-height;
    }
  }

  &__date-cell {
    position: absolute;
    height: $mobile-cell-height;
    width: $mobile-cell-height;
    border-radius: $global-border-radius;

    @include laptopAndLarge() {
      height: $laptop-cell-height;
      width: $laptop-cell-height;
    }

    &--is-from {
      border-radius: $global-border-radius 0 0 $global-border-radius;
    }

    &--is-to {
      border-radius: 0 $global-border-radius $global-border-radius 0;
    }

    &--is-both {
      border-radius: $global-border-radius;
    }

    &--selected {
      background-color: getColor('primary') !important;
    }

    &--today:not(&--selected) {
      background-color: getColor('note') !important;
    }

    &:hover:not(&--disabled) {
      background-color: getColor('grey100');
    }

    &--disabled {
      pointer-events: none;
    }
  }

  &__block {
    width: 50%;
    height: inherit;

    &--is-from:nth-of-type(1) {
      background-color: unset;
    }

    &--is-from {
      border-radius: $global-border-radius 0 0 $global-border-radius;
    }

    &--is-to {
      border-radius: 0 $global-border-radius $global-border-radius 0;
    }

    &--is-to:nth-of-type(2) {
      background-color: unset;
    }

    &--highlighted {
      background-color: getColor('primaryTransparent');
    }
  }
}
</style>
